import * as THREE from 'three';
import { between, type Random } from './critter-math.js';
import type { BirdSpecies } from './bird-species.js';
import { PathFlight } from './path-flight.js';

export interface FlyoverPlan { from: THREE.Vector3; to: THREE.Vector3 }

const ray = new THREE.Raycaster();
/** The point on the horizontal plane `altitude` metres up that appears at normalised screen position `(x, y)`. */
export function unprojectAt(camera: THREE.OrthographicCamera, x: number, y: number, altitude: number, into = new THREE.Vector3()) {
  camera.updateMatrixWorld();
  ray.setFromCamera(new THREE.Vector2(x, y), camera);
  // Orthographic rays are parallel and may begin on either side of the plane, so use the infinite line.
  return ray.ray.at((altitude - ray.ray.origin.y) / ray.ray.direction.y, into);
}

/**
 * A line across the camera's view, at height, from just outside one edge of the screen to just outside the other.
 * It is planned in screen space so it always crosses what the player is looking at, wherever the camera is, but flown in
 * world space so it carries on in its own direction whatever the camera then does. Mostly side to side, sometimes top to bottom.
 */
export function planFlyover(camera: THREE.OrthographicCamera, random: Random, altitude: number): FlyoverPlan {
  const flip = random() < .5 ? -1 : 1;
  let a: [number, number], b: [number, number];
  if (random() < .78) { const y = between(random, -.5, .75); a = [-1.35 * flip, y]; b = [1.35 * flip, y + between(random, -.35, .35)]; }
  else { const x = between(random, -.6, .6); a = [x, 1.35 * flip]; b = [x + between(random, -.4, .4), -1.35 * flip]; }
  return { from: unprojectAt(camera, a[0], a[1], altitude), to: unprojectAt(camera, b[0], b[1], altitude) };
}

/**
 * One bird of a flock crossing overhead. It flies a straight track with its own slow weave, a lag behind its leader,
 * alternating bursts of flapping with glides, banking into the weave and rising and falling with it.
 */
export class FlyoverBird {
  readonly position = new THREE.Vector3();
  done = false;
  private travelled: number;
  private readonly flight: PathFlight;
  private readonly length: number; private readonly direction: THREE.Vector3; private readonly side: THREE.Vector3;
  private readonly phase: number; private readonly weave: number;

  /** `lag` metres behind the leader, `offset` metres to the side and `lift` metres above the track. */
  constructor(species: BirdSpecies, private plan: FlyoverPlan, private speed: number, lag: number, private offset: number, private lift: number, random: Random) {
    this.direction = plan.to.clone().sub(plan.from); this.length = this.direction.length(); this.direction.divideScalar(this.length || 1);
    this.side = new THREE.Vector3(-this.direction.z, 0, this.direction.x).normalize();
    this.travelled = -lag; this.phase = random() * Math.PI * 2; this.weave = between(random, .07, .11);
    this.flight = new PathFlight(species, random); this.flight.heading = Math.atan2(this.direction.x, this.direction.z);
    this.place(this.travelled, this.position);
  }

  get action() { return this.flight.action; }
  get heading() { return this.flight.heading; }

  /** Where this bird is once it has flown `s` metres of the track. */
  private place(s: number, into: THREE.Vector3) {
    const sideways = this.offset + Math.sin(s * this.weave + this.phase) * 1.4;
    return into.copy(this.plan.from).addScaledVector(this.direction, s).addScaledVector(this.side, sideways)
      .setY(this.plan.from.y + this.lift + Math.sin(s * this.weave * 1.6 + this.phase * 1.7) * .7);
  }

  private ahead = new THREE.Vector3(); private later = new THREE.Vector3();
  update(dt: number) {
    this.travelled += this.speed * dt;
    this.place(this.travelled, this.position); this.place(this.travelled + 1.5, this.ahead); this.place(this.travelled + 4, this.later);
    this.flight.follow(dt, this.position, this.ahead, this.later);
    if (this.travelled >= this.length) this.done = true;
  }
}
